import {useState, useEffect, useCallback, useMemo, useRef} from "react";
import { Col, Row, useIsMobile } from "../../components/Flexbox";
import FilterBar from "../../components/FilterBar";
import Text from "../../components/Text";
import { useTheme } from "../../scripts/functions/ThemeProvider";
import SearchBox from "../../components/SearchBox";
import CustomerAndRequest from "../../components/CustomerAndRequest";
import TechSpecs from "../../components/TechSpecs";
import PricesAndControl from "../../components/PricesAndControl";
import { getFiles } from "../../containers/BudgetNormalizer";
import { DateFormatter } from "../../utils";
import { useForm, useWatch } from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import { budgetSchema } from "../../scripts/configs/BudgetConfig";
import { NotificationManager } from "../../scripts/functions/NotificationManager";
import { BudgetPersistance } from "../../scripts/functions/BudgetPersistance";
import { GetLocalDrafts } from "../../scripts/functions/GetLocalDrafts";
import { loadFromStorage, useSaveToStorage } from "../../utils";
import {useBudgets, useSaveBudget, useRequestApproval } from "../../scripts/queries/useBudgetQueries";

// Avoid a new array render when query data is undefined
const EMPTY_ARRAY = [];

const INITIAL_FORM = {
    //Metadata
    id: "", status: "Borrador", checkedAt: "", approved: false, requestedApproval: false,
    approvalDate: null,

    //CustomerAndRequest
    original: "", format: "Caja", urgency: "", remunerationGroup: "", customerAccepts: "",    //Dropdowns
    title: "", controlNum: "", partNum: "", deliverTo: "", atn: "", phone:"",
    mail: "", package: "", freight: "", piecesXSleeve: "", piecesXBox: "", casesXPallet: "",
    rebase: false, solid: false, backup: false, colorGamut: false, fileFormat: "", sheet: "", printSize: "",
    gangRun: "", customerNotes: "",
    customerSearch: "", //Searchbox
    requirements: false, certMatPr: false, certProdTerm: false, newProc: false, newMat: false, //Checkbox

    //TechSpecs
    //DimensionAndTolerance
    flute: "", //Dropdowns
    flatSize: "", finalSize: "", chain: "", front: "", side: "", height: "", tolerance: "",   //TextFields
    //SubstratumAndInk
    frontInk: "", backInk: "", pages: "", frontPage: "", interior: "", //TextFields
    substrates: [{substrate: "", grammage: "", rollWidth: ""}],
    //ProductiveProcess
    activeProcesses: [],
    //ProductionParams
    press: "", whiteUnderprint: "", coating: {line: [], application: [], faces: []}, sleeves: "", //Dropdowns
    inkMixTxt: "", clickChargeTxt1: "", clickChargeTxt2: "", additionalSub: "", chemicalsTxt: "", splice: "", //TextFields
    tradePrint: "", across: "", around: "", design: "", ctpTxt: "", cutTxt: "", shapeCutTxt: "", positiveTxt: "",
    //Finishes
    activeFinishes: [],
    //FinishesParams
    dieCut: "", tool: "", glue: "", glueFlap: "", coatingFinish: {line: [], application: [], faces: []}, laminationTxt: {type: [], application: [], faces: []},
    //TechSpecsEnd
    rewindType: "", labelsAcross: "", labelsXRoll: "", rewindOrientation: "", techNotes: "",

    //PricesAndControl
    paper: "", plates: "", toolCost: "", cost: "", secondProc: "", opProc: "",
    authQuantity: "", unitPrice: "", dieCutPrice: "", urgencyPrice: "", total: "", budget: "", control: "", deposit: "",
    invoice: "", order: "", startDate: "", dueDate: "", lastShip: "", attended: "", cotized: "",
    submitted: "", checked: "", pricesNotes: "",
    costXQuantity: [{quantity: "", price: ""}, {quantity: "", price: ""}, {quantity: "", price: ""}],
};

const FIELD_LABELS = {
    controlNum: "Número de control", phone: "Teléfono", mail: "Correo electrónico", piecesXSleeve: "Piezas por fajilla", piecesXBox: "Piezas por caja",
    casesXPallet: "Cajas por tarima", substrate: "Sustrato", grammaje: "Gramaje", rollWidth: "Ancho de bobina", across: "Figuras al paso",
    around: "Figuras alrededor", labelsAcross: "Etiquetas al paso", labelsXRoll: "Etiquetas por rollo", rewindOrientation: "Orientación de rebobinado",
    paper: "Papel", plates: "Placas", toolCost: "Costo de herramienta", cost: "Costo", secondProc: "Segundo Proceso", opProc: "Proceso opcional",
    authQuantity: "Cantidad autorizada", unitPrice: "Precio unitario", dieCutPrice: "Suaje", urgency: "Urgencia", total: "Total", budget: "Presupuesto",
    control: "Número de control", deposit: "Anticipo", startDate: "Fecha de ingreso", dueDate: "Fecha de entrega", lastShip: "Fecha de último embarque",
    quantity: "Cantidad", price: "Precio", tolerance: "Tolerancia"
}

const Presupuestos = () => {
    const persisted = loadFromStorage('SELECTED_BUDGET') || {};
    const {theme} = useTheme();
    const isMobile = useIsMobile();
    const [activeOption, setActiveOption] = useState(typeof persisted === 'string' ? persisted : "Solicitar Presupuesto");
    const [search, setSearch] = useState("");
    const [currentDraftId, setCurrentDraftId] = useState(null);
    const [activeContainer, setActiveContainer] = useState("Solicitud y cliente");
    const {handleSubmit, setValue, getValues, reset, control, watch, formState} = useForm({resolver: zodResolver(budgetSchema), defaultValues: INITIAL_FORM})

    const {isDirty} = formState;
    const {data: s3Budgets = EMPTY_ARRAY} = useBudgets();
    const saveMutation = useSaveBudget();
    const requestApproval = useRequestApproval();

    useSaveToStorage('SELECTED_BUDGET', activeOption);
    
    //Blocks the draft autosave while a draft is being loaded into the form
    const isLoadingRef = useRef(false);
    const {getDraft, clearDraft} = BudgetPersistance(currentDraftId, watch, isDirty, isLoadingRef);

    const controlNum = useWatch({control, name: "controlNum"});
    const customerSearch = useWatch({control, name: "customerSearch"});
    const title = useWatch({control, name: "title"});
    const status = useWatch({control, name: "status"})
    
    const handleChange = useCallback((name, value) => { //Updates the value in RHF and launches Zod validations
        setValue(name, value, {shouldValidate: true});
    }, [setValue]);

    const budgets = useMemo(() => {
        const localDrafts = GetLocalDrafts() || [];

        return [...localDrafts, ...s3Budgets].sort((a, b) => {
            const dateA = new Date(a._lastModified || a.createdAt || 0);
            const dateB = new Date(b._lastModified || b.createdAt || 0);
            return dateB - dateA;
        });
    }, [s3Budgets]);

    const onValidSubmit = async (data) => {
        saveMutation.mutate(data, {
            onSuccess: () => {
                clearDraft();
            }
        })
    }

    const onInvalidSubmit = (errors) => {
        const extractMessages = (msg) => {
            let messages = [];

            //Extract the messages
            for (const key in msg) {
                const error = msg[key];
                if (error?.message) {
                    const label = FIELD_LABELS[key] || key;
                    let formattedMsg = error.message;

                    if (formattedMsg.includes("expected number") || formattedMsg.includes("NaN")) {
                        formattedMsg = "Debe ser un número";
                    } else if (formattedMsg.includes("Invalid input")) {
                        formattedMsg = "Formato o valor no válido";
                    }
                    const finalMsg = formattedMsg.toLowerCase().includes("obligatorio") ? formattedMsg : `${label}: ${formattedMsg}`;
                    messages.push(finalMsg);

                //If there's an object or array, use recursivity
                } else if (typeof error === 'object' && error !== null) {
                    messages = messages.concat(extractMessages(error));
                }
            }
            return messages;
        };

        const allMessages = [...new Set(extractMessages(errors))];

        //Separate mandatory errors from special ones (like data type errors)
        const mandatory = allMessages.filter(m => m.includes("obligatorio"));
        const special = allMessages.filter(m => !m.includes("obligatorio"));

        const notify = (msg, index) => {
            setTimeout(() => {
                NotificationManager.push(msg, false);
            }, index * 150);
        }

        special.forEach((m, i) => notify(m, i));

        //Avoid showing too many notifications
        const offset = special.length;
        if (mandatory.length > 4) {
            notify(`Faltan ${mandatory.length} campos obligatorios`, offset);
        } else {
            mandatory.forEach((m, i) => notify(m, i + offset));
        }
    }

    useEffect(() => {
        if (controlNum) {
            setValue("id", controlNum, {shouldValidate: true});
        }

    }, [controlNum, setValue, getValues]);

    const handleContainers = (text) => {
        setActiveContainer(text);
    }

    const stateColor = (state) => {
        if (state === "Presupuestar") {
            return theme.Magenta
        } else if (state === "Presupuestado") {
            return theme.Verde
        } else if (state === "Ingresado") {
            return theme.Blue
        }
    }

    const getDate = (prefix) => {
        if (!prefix) return "";
        
        if (!prefix.includes("Budget on: ")) {
            const dateObject = new Date(prefix);
            if (!isNaN(dateObject.getTime())) {
                return DateFormatter(dateObject, 'COMPACT_DATE');
            }
        }

        const date = prefix.replace("Budget on: ", "").slice(0, -1);

        const parts = date.split(' ');
        let correctDate;

        //Apply the UTC format to the date before sending it to DateFormatter
        if (parts.length >= 4) {
            correctDate = `${parts[1]} ${parts[0]} ${parts[2]} ${parts[3]} UTC`
        } else {
            correctDate = date + ' UTC';
        }

        const dateObject = new Date(correctDate);

        if(isNaN(dateObject.getTime())) {
            console.error("Invalid Date created for forecast:", date);

            return "Error de formato de fecha";
        }

        return DateFormatter(dateObject, 'COMPACT_DATE');
    }

    const addCosts = () => {
        const currentCosts = getValues("costXQuantity") || [];

        if (currentCosts.length < 6) {
            const newRow = [
                {quantity: "", price: ""},
                {quantity: "", price: ""},
                {quantity: "", price: ""}
            ];

            setValue("costXQuantity", [...currentCosts, ...newRow], {shouldValidate: true})
        }
    }

    useEffect(() => {
        //Held until the incoming draft is in the form, so the autosave can't write these
        //values under the previously selected draft's id
        isLoadingRef.current = true;

        const initLoad = async () => {
            if (activeOption === "Solicitar Presupuesto") {
                //Generate the prefix for the draft
                const currentDate = new Date();
                const formatter = new Intl.DateTimeFormat('en-GB', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false});
                const newId = `Budget on: ${formatter.format(currentDate).replace(',', '')}`
                setCurrentDraftId(newId);
                reset(INITIAL_FORM);
                isLoadingRef.current = false;
            } else {
                const [source, prefix] = activeOption.split('|');
                setCurrentDraftId(prefix);

                if (source === 'local') {
                    // console.log("Loading from local");
                    const draft = getDraft(prefix);
                    reset(draft);
                    isLoadingRef.current = false;
                } else {
                    getFiles(prefix)
                    .then(budgetData => {
                        // console.log("budgetdata: ", budgetData)
                        if (budgetData) {
                            reset(budgetData);
                        } else {
                            console.warn("No se encontro el JSON en S3");
                            reset(INITIAL_FORM);
                        }
                    })
                    .catch(e => {
                        console.error("Error al cargar el presupuesto: ", e);
                        reset(INITIAL_FORM);
                    })
                    .finally(() => {
                        isLoadingRef.current = false;
                    })
                }
            }
        }
        initLoad();
    }, [activeOption, getDraft, reset])

    return (
        <Row style={{ width:'100%', flex:'none',alignItems: isMobile ? 'stretch' : 'flex-start', paddingLeft: isMobile ? 16 : 64, paddingRight: isMobile ? 16 : 64, minHeight: '100vh', overflow: 'visible', flexDirection: isMobile ? 'column' : 'row'}}>
            {isMobile ? (
                <Col style={{width: '100%', minWidth: 0, flex: 'none', marginTop: 32, justifyContent: 'start', alignItems: 'start', gap: 10}}>
                    <Col onClick={() => setActiveOption("Solicitar Presupuesto")} style={{cursor:'pointer', backgroundColor: activeOption === "Solicitar Presupuesto" ? `rgba(${parseInt(theme.Magenta.slice(1, 3), 16)}, ${parseInt(theme.Magenta.slice(3, 5), 16)}, ${parseInt(theme.Magenta.slice(5, 7), 16)}, 0.05)` : theme.Checo,  border:'solid 1px', borderColor: activeOption === "Solicitar Presupuesto" ? theme.Magenta : theme.Coco, width:'100%', height:52, flex:'none', borderRadius:4, alignItems: 'flex-start', padding:16}}>
                        <Text color={activeOption === "Solicitar Presupuesto" ? theme.Magenta : theme.ONED}>Solicitar Presupuesto</Text>
                    </Col>
                    <SearchBox theme={theme}  style={{width: '100%', height: 35}} value = {search} onChange={setSearch}></SearchBox>
                    {budgets.length > 0 && (
                        <FilterBar style={{width: '100%', gap: 12, alignItems: 'stretch'}}>
                            {budgets.map(budget => {
                                const id = `${budget.isLocal ? 'local' : 's3'}|${budget.prefix}`
                                return (
                                    <Col key={id} onClick={() => setActiveOption(id)} style={{cursor:'pointer', backgroundColor: activeOption === id ? `rgba(${parseInt(theme.Magenta.slice(1, 3), 16)}, ${parseInt(theme.Magenta.slice(3, 5), 16)}, ${parseInt(theme.Magenta.slice(5, 7), 16)}, 0.05)` : theme.Checo,  border:'solid 1px', borderColor: activeOption === id ? theme.Magenta : theme.Coco, width:'240px', minHeight:52, flex:'none', borderRadius:4, alignItems:'flex-start', padding:16}}>
                                        <Row style={{alignItems: 'flex-start', width: '100%'}}>
                                            <Col style={{justifyContent:'space-between', width: '100%', maxHeight: 127}}>
                                                <Row style={{width: '100%', height: 19, gap: 8, justifyContent: 'space-between'}}>
                                                    <Text style={{textAlign:'left'}} size={15} color={theme.ONED}>{budget.name}</Text>
                                                    <Text size={budget.status === "Borrador" ? 10 : 12} color={activeOption === budget.prefix ? theme.Magenta : theme.ONED}>{budget.id !== "" ? budget.id : "Borrador"}</Text>
                                                </Row>
                                                <Col style={{width: '100%', height: 30, gap: 2, marginTop: 5}}>
                                                    <Text style={{width: '100%', textAlign:'left'}} size={10} color={theme.ONED}>Cliente:</Text>
                                                    <Text style={{width: '100%', textAlign:'left'}} size={12} color={theme.Lao}>{budget.customer}</Text>
                                                </Col>
                                                <Row style={{width: '100%', height: 30, marginTop: 8, justifyContent: 'space-between'}}>
                                                    <Col style={{gap: 2, alignItems: 'start'}}>
                                                        <Text size={10} color={theme.ONED}>Solicitado</Text>
                                                        <Text size={12} color={theme.Lao}>{getDate(budget.isLocal ? budget._lastModified : budget.prefix)}</Text>
                                                    </Col>
                                                    <Col style={{alignItems: 'end'}}>
                                                        <Text style={{visibility: (budget.status !== "Borrador" ? 'visible' : 'hidden')}} size={10} color={stateColor(budget.status)}>{budget.status}</Text>
                                                        <Text size={12} color={theme.Lao}>{budget.checkedAt === "" ? budget.status : budget.checkedAt}</Text>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Col>
                                )
                            })}
                        </FilterBar>
                    )}
                </Col>
            ) : (
            <Col style={{maxWidth: '316px', minWidth: '316px', height: '100vh', position: 'sticky', top: 0, justifyContent: 'start', alignItems: 'start', flex: 'none'}}>
                <Col style={{maxHeight: '14%', marginTop: '32px', minWidth: '100%', justifyContent: 'start'}}>
                    <Col onClick={() => setActiveOption("Solicitar Presupuesto")} style={{cursor:'pointer', backgroundColor: activeOption === "Solicitar Presupuesto" ? `rgba(${parseInt(theme.Magenta.slice(1, 3), 16)}, ${parseInt(theme.Magenta.slice(3, 5), 16)}, ${parseInt(theme.Magenta.slice(5, 7), 16)}, 0.05)` : theme.Checo,  border:'solid 1px', borderColor: activeOption === "Solicitar Presupuesto" ? theme.Magenta : theme.Coco, width:'100%', height:52, flex:'none', borderRadius:4, alignItems: 'flex-start', padding:16}}>
                        <Text color={activeOption === "Solicitar Presupuesto" ? theme.Magenta : theme.ONED}>Solicitar Presupuesto</Text>
                    </Col>
                    <SearchBox theme={theme}  style={{width: '100%', height: 35, marginTop: 10}} value = {search} onChange={setSearch}></SearchBox>
                </Col>
                <div style={{width: '100%', minHeight: '12px', flexShrink: 0}}></div>
                <Col style={{height: '100%', overflowY: 'auto', justifyContent: 'start', maxWidth: '316px', flex: 'none', scrollbarWidth: 'none', flexShrink: 0, position: 'sticky'}}>
                    {budgets.length > 0 && budgets.map(budget => {
                        const id = `${budget.isLocal ? 'local' : 's3'}|${budget.prefix}`
                        return (
                            <Col key={id} onClick={() => setActiveOption(id)} style={{cursor:'pointer', marginTop: 10, backgroundColor: activeOption === id ? `rgba(${parseInt(theme.Magenta.slice(1, 3), 16)}, ${parseInt(theme.Magenta.slice(3, 5), 16)}, ${parseInt(theme.Magenta.slice(5, 7), 16)}, 0.05)` : theme.Checo,  border:'solid 1px', borderColor: activeOption === id ? theme.Magenta : theme.Coco, width:'316px', minHeight:52, flex:'none', borderRadius:4, alignItems:'flex-start', padding:16}}>
                                <Row style={{alignItems: 'flex-start', width: '100%'}}>
                                    <Col style={{justifyContent:'space-between', width: '100%', maxHeight: 127}}>
                                        <Row style={{width: '100%', height: 19, gap: 8, justifyContent: 'space-between'}}>
                                            <Text style={{textAlign:'left'}} size={15} color={theme.ONED}>{budget.name}</Text>
                                            <Text size={budget.status === "Borrador" ? 10 : 12} color={activeOption === budget.prefix ? theme.Magenta : theme.ONED}>{budget.id !== "" ? budget.id : "Borrador"}</Text>
                                        </Row>
                                        <Col style={{width: '100%', height: 30, gap: 2, marginTop: 5}}>
                                            <Text style={{width: '100%', textAlign:'left'}} size={10} color={theme.ONED}>Cliente:</Text>
                                            <Text style={{width: '100%', textAlign:'left'}} size={12} color={theme.Lao}>{budget.customer}</Text>
                                        </Col>
                                        <Row style={{width: '100%', height: 30, marginTop: 8, justifyContent: 'space-between'}}>
                                            <Col style={{gap: 2, alignItems: 'start'}}>
                                                <Text size={10} color={theme.ONED}>Solicitado</Text>
                                                <Text size={12} color={theme.Lao}>{getDate(budget.isLocal ? budget._lastModified : budget.prefix)}</Text>
                                            </Col>
                                            <Col style={{alignItems: 'end'}}>
                                                <Text style={{visibility: (budget.status !== "Borrador" ? 'visible' : 'hidden')}} size={10} color={stateColor(budget.status)}>{budget.status}</Text>
                                                <Text size={12} color={theme.Lao}>{budget.checkedAt === "" ? budget.status : budget.checkedAt}</Text>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </Col>
                        )
                    })}
                </Col>
            </Col>
            )}
            <Col style={{ width: isMobile ? '100%' : undefined, minWidth: 0, alignItems:'flex-start', paddingLeft: isMobile ? 0 : 32, paddingTop: 32, paddingBottom: 64, overflow: 'visible'}}>
                <Col style={{height: 65, flex:'none', width: '100%', alignItems:'flex-start', justifyContent: 'space-between', paddingBottom:32}}>
                    <Row style={{width: '100%', justifyContent: 'space-between'}}>
                        <Text color={theme.Black} size={21} weight={"extrabold"}>Solicitar Presupuesto</Text>
                        {activeContainer !== "Solicitud y cliente" && (
                            <Row style={{flex: 1, justifyContent: 'end'}}>
                                <Text size={15} color={theme.SEVENB}>{`${title}, ${customerSearch} · ${status} · `  || ""}</Text>
                                <Text size={15} color={theme.Magenta} onClick={handleSubmit(onValidSubmit, onInvalidSubmit)} style={{ cursor:'pointer', transition:'transform 2s ease'}}> &nbsp; Solicitar</Text>
                            </Row>
                        )}
                    </Row>
                    <Row style={{height: 20, width: '100%', maxWidth: 447, justifyContent: 'space-between', paddingTop: 30}}>
                        <Text style={{ cursor:'pointer', transition:'transform 2s ease'}} color={activeContainer === "Solicitud y cliente" ? theme.Magenta : theme.Lao} onClick={() => handleContainers("Solicitud y cliente")} size={15}>Solicitud y cliente</Text>
                        <Text style={{ cursor:'pointer', transition:'transform 2s ease'}} color={activeContainer === "Especificaciones técnicas" ? theme.Magenta : theme.Lao} onClick={() => handleContainers("Especificaciones técnicas")} size={15}>Especificaciones técnicas</Text>
                        <Text style={{ cursor:'pointer', transition:'transform 2s ease'}} color={activeContainer === "Precios y control" ? theme.Magenta : theme.Lao} onClick={() => handleContainers("Precios y control")} size={15}>Precios y control</Text>
                    </Row>
                </Col>
                {activeContainer === "Solicitud y cliente" && (
                    <CustomerAndRequest theme={theme} control={control}/>
                )}
                {activeContainer === "Especificaciones técnicas" && (
                    <TechSpecs theme={theme} control={control} onChange={handleChange}/>
                )}
                {activeContainer === "Precios y control" && (
                    <PricesAndControl theme={theme} control={control} addCosts={addCosts} getApproval={requestApproval.mutate} setValue={setValue}/>
                )}
            </Col>
        </Row>
    )
}

export default Presupuestos;